import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Divider,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import PageBackHeader from '../components/PageBackHeader';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SellIcon from '@mui/icons-material/Sell';
import NotesIcon from '@mui/icons-material/Notes';
import { customerDirectoryService, productService, productKitService } from '../services/api';

const SHIPPING_OPTIONS = [
  { value: 'self', label: 'Самовивіз' },
  { value: 'nova_poshta', label: 'Нова пошта' },
  { value: 'our_truck', label: 'Наша машина' },
];

const DEFAULT_PAYMENT_OPTIONS = [
  { value: 'cash', label: 'Готівка' },
  { value: 'cashless', label: 'Безготівка' },
  { value: 'prepayment', label: 'Передплата' },
];

let tempKeySeq = 0;
const nextTempKey = () => {
  tempKeySeq += 1;
  return `t${tempKeySeq}`;
};

function SectionHeader({ icon, title }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      {icon}
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
  );
}

const fieldRowSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2,
};

function CustomerEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [products, setProducts] = useState([]);
  const [priceRows, setPriceRows] = useState([]);
  const [productPickerOpenKey, setProductPickerOpenKey] = useState(null);
  const [kits, setKits] = useState([]);
  const [kitPriceRows, setKitPriceRows] = useState([]);
  const [form, setForm] = useState({
    name: '',
    contact_full_name: '',
    contact_phone: '',
    contact_email: '',
    contact_birth_date: '',
    default_payment_type: '',
    address: '',
    delivery_address: '',
    shipping_method: 'self',
    notes: '',
    is_active: true,
  });

  const productOptions = useMemo(
    () =>
      (products || []).map((p) => ({
        id: p.id,
        article: p.article || '',
        label: p.article ? `${p.article} — ${p.name}` : p.name,
      })),
    [products],
  );

  const productByArticle = useMemo(() => {
    const map = new Map();
    productOptions.forEach((p) => {
      const k = String(p.article || '').trim().toLowerCase();
      if (k) map.set(k, p);
    });
    return map;
  }, [productOptions]);

  const loadProducts = useCallback(async () => {
    const res = await productService.getAll();
    const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
    setProducts(list);
  }, []);

  const loadKits = useCallback(async () => {
    const res = await productKitService.getAll({ is_active: true });
    const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
    setKits(list);
  }, []);

  useEffect(() => {
    loadProducts();
    loadKits();
  }, [loadProducts, loadKits]);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await customerDirectoryService.getById(id);
        const c = res.data;
        if (cancelled) return;
        setForm({
          name: c.name || '',
          contact_full_name: c.contact_full_name || '',
          contact_phone: c.contact_phone || '',
          contact_email: c.contact_email || '',
          contact_birth_date: c.contact_birth_date ? String(c.contact_birth_date).slice(0, 10) : '',
          default_payment_type: c.default_payment_type || '',
          address: c.address || '',
          delivery_address: c.delivery_address || '',
          shipping_method: c.shipping_method || 'self',
          notes: c.notes || '',
          is_active: c.is_active !== false,
        });
        const rows = (c.product_prices || []).map((pp) => ({
          tempKey: nextTempKey(),
          product: pp.product,
          article: pp.product_article || '',
          price_form1: pp.price_form1 != null ? String(pp.price_form1) : '',
          price_form2: pp.price_form2 != null ? String(pp.price_form2) : '',
        }));
        setPriceRows(rows);
        const kitRows = (c.kit_prices || []).map((kp) => ({
          tempKey: nextTempKey(),
          kit: kp.kit,
          kit_name: kp.kit_name || '',
          price_form1: kp.price_form1 != null ? String(kp.price_form1) : '',
          price_form2: kp.price_form2 != null ? String(kp.price_form2) : '',
        }));
        setKitPriceRows(kitRows);
      } catch (e) {
        console.error(e);
        alert('Не вдалося завантажити замовника');
        navigate('/customer-orders?tab=customers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  const addPriceRow = () => {
    setPriceRows((prev) => [
      ...prev,
      { tempKey: nextTempKey(), product: '', article: '', price_form1: '', price_form2: '' },
    ]);
  };

  const removePriceRow = (key) => {
    setPriceRows((prev) => prev.filter((r) => r.tempKey !== key));
  };

  const updatePriceRow = (key, field, value) => {
    setPriceRows((prev) => prev.map((r) => (r.tempKey === key ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Вкажіть назву');
      return;
    }

    const product_prices = priceRows
      .filter((r) => r.product)
      .map((r) => {
        const f1 = r.price_form1 === '' || r.price_form1 == null ? null : parseFloat(r.price_form1);
        const f2 = r.price_form2 === '' || r.price_form2 == null ? null : parseFloat(r.price_form2);
        return {
          product: Number(r.product),
          price_form1: Number.isFinite(f1) ? f1 : null,
          price_form2: Number.isFinite(f2) ? f2 : null,
        };
      });

    const kit_prices = kitPriceRows
      .filter((r) => r.kit)
      .map((r) => ({
        kit: Number(r.kit),
        price_form1: r.price_form1 === '' || r.price_form1 == null ? null : parseFloat(r.price_form1),
        price_form2: r.price_form2 === '' || r.price_form2 == null ? null : parseFloat(r.price_form2),
      }));

    const payload = {
      name: form.name.trim(),
      contact_full_name: (form.contact_full_name || '').trim(),
      contact_phone: (form.contact_phone || '').trim(),
      contact_email: (form.contact_email || '').trim(),
      contact_birth_date: form.contact_birth_date ? form.contact_birth_date : null,
      default_payment_type: form.default_payment_type || '',
      address: form.address || '',
      delivery_address: form.delivery_address || '',
      shipping_method: form.shipping_method,
      notes: form.notes || '',
      is_active: form.is_active,
      product_prices,
      kit_prices,
    };

    try {
      setSaving(true);
      if (isNew) {
        const res = await customerDirectoryService.create(payload);
        navigate(`/customers/${res.data.id}`, { replace: true });
      } else {
        await customerDirectoryService.update(id, payload);
        setSnackbar({ open: true, message: 'Збережено', severity: 'success' });
      }
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data &&
        (typeof e.response.data === 'string'
          ? e.response.data
          : JSON.stringify(e.response.data, null, 2));
      setSnackbar({
        open: true,
        message: `Помилка збереження: ${msg || e.message || 'Невідома помилка'}`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={6}>
        <CircularProgress />
        <Typography color="text.secondary">Завантаження…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', pb: 10 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <PageBackHeader
            to="/customer-orders?tab=customers"
            label="До списку замовників"
            title={isNew ? 'Новий замовник' : form.name || 'Картка замовника'}
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          }
          label={form.is_active ? 'Активний' : 'Неактивний'}
        />
      </Stack>

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader icon={<BusinessIcon color="primary" fontSize="small" />} title="Реквізити" />
          <Stack spacing={2}>
            <TextField
              label="Назва"
              fullWidth
              required
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <Box sx={fieldRowSx}>
              <TextField
                label="ПІБ контактної особи"
                fullWidth
                value={form.contact_full_name}
                onChange={(e) => setForm((p) => ({ ...p, contact_full_name: e.target.value }))}
              />
              <TextField
                label="Дата народження"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.contact_birth_date}
                onChange={(e) => setForm((p) => ({ ...p, contact_birth_date: e.target.value }))}
              />
            </Box>
            <Box sx={fieldRowSx}>
              <TextField
                label="Телефон контактної особи"
                fullWidth
                value={form.contact_phone}
                onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))}
              />
              <TextField
                label="E-mail контактної особи"
                fullWidth
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={fieldRowSx}>
              <TextField
                select
                label="Типові умови оплати"
                fullWidth
                value={form.default_payment_type}
                onChange={(e) => setForm((p) => ({ ...p, default_payment_type: e.target.value }))}
              >
                <MenuItem value="">—</MenuItem>
                {DEFAULT_PAYMENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Спосіб доставки"
                fullWidth
                value={form.shipping_method}
                onChange={(e) => setForm((p) => ({ ...p, shipping_method: e.target.value }))}
                InputProps={{
                  startAdornment: <LocalShippingIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              >
                {SHIPPING_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={fieldRowSx}>
              <TextField
                label="Адреса"
                fullWidth
                multiline
                minRows={2}
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              />
              <TextField
                label="Адреса доставки"
                fullWidth
                multiline
                minRows={2}
                value={form.delivery_address}
                onChange={(e) => setForm((p) => ({ ...p, delivery_address: e.target.value }))}
              />
            </Box>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SellIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Ціни на вироби
              </Typography>
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={addPriceRow}>
              Додати рядок
            </Button>
          </Stack>

          {priceRows.length === 0 ? (
            <Box
              sx={{
                border: '1px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
              }}
            >
              <Typography color="text.secondary">Немає виробів — натисніть «Додати рядок»</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '14%', whiteSpace: 'nowrap' }}>Артикул</TableCell>
                    <TableCell>Виріб</TableCell>
                    <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>Ціна (Форма 1)</TableCell>
                    <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>Ціна (Форма 2)</TableCell>
                    <TableCell sx={{ width: 40, maxWidth: 40, p: '4px !important' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {priceRows.map((row) => {
                    const rowProductOpt =
                      productOptions.find((p) => Number(p.id) === Number(row.product)) || null;
                    return (
                      <TableRow key={row.tempKey} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Артикул"
                            value={row.article}
                            onChange={(e) => {
                              const article = e.target.value;
                              const key = String(article || '').trim().toLowerCase();
                              const exact = key ? productByArticle.get(key) : null;
                              if (exact) {
                                setPriceRows((prev) =>
                                  prev.map((r) =>
                                    r.tempKey === row.tempKey
                                      ? { ...r, article: exact.article || article, product: exact.id }
                                      : r,
                                  ),
                                );
                                setProductPickerOpenKey(null);
                                return;
                              }
                              setPriceRows((prev) =>
                                prev.map((r) => (r.tempKey === row.tempKey ? { ...r, article } : r)),
                              );
                              setProductPickerOpenKey(row.tempKey);
                            }}
                            onFocus={() => {
                              if ((row.article || '').trim()) setProductPickerOpenKey(row.tempKey);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={(() => {
                              const q = String(row.article || '').trim().toLowerCase();
                              if (!q) return productOptions;
                              return productOptions.filter((p) =>
                                String(p.article || '').trim().toLowerCase().includes(q),
                              );
                            })()}
                            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                            getOptionLabel={(o) => o.label || ''}
                            open={productPickerOpenKey === row.tempKey}
                            onOpen={() => setProductPickerOpenKey(row.tempKey)}
                            onClose={() => {
                              if (productPickerOpenKey === row.tempKey) setProductPickerOpenKey(null);
                            }}
                            value={rowProductOpt}
                            onChange={(_, opt) => {
                              setPriceRows((prev) =>
                                prev.map((r) =>
                                  r.tempKey === row.tempKey
                                    ? {
                                        ...r,
                                        product: opt ? opt.id : '',
                                        article: opt ? opt.article || '' : r.article,
                                      }
                                    : r,
                                ),
                              );
                              setProductPickerOpenKey(null);
                            }}
                            renderInput={(params) => <TextField {...params} placeholder="Оберіть виріб" />}
                            sx={{ minWidth: 0, width: '100%' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={row.price_form1}
                            onChange={(e) => updatePriceRow(row.tempKey, 'price_form1', e.target.value)}
                            inputProps={{ min: 0, step: 'any' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={row.price_form2}
                            onChange={(e) => updatePriceRow(row.tempKey, 'price_form2', e.target.value)}
                            inputProps={{ min: 0, step: 'any' }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() => removePriceRow(row.tempKey)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SellIcon color="secondary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>
                Ціни на комплекти
              </Typography>
            </Stack>
            <Button size="small" startIcon={<AddIcon />} onClick={() =>
              setKitPriceRows((prev) => [
                ...prev,
                { tempKey: nextTempKey(), kit: '', kit_name: '', price_form1: '', price_form2: '' },
              ])
            }>
              Додати рядок
            </Button>
          </Stack>

          {kitPriceRows.length === 0 ? (
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Немає комплектів — натисніть «Додати рядок»</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 540 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Комплект</TableCell>
                    <TableCell sx={{ width: '18%', whiteSpace: 'nowrap' }}>Ціна (Форма 1)</TableCell>
                    <TableCell sx={{ width: '18%', whiteSpace: 'nowrap' }}>Ціна (Форма 2)</TableCell>
                    <TableCell sx={{ width: 40, maxWidth: 40, p: '4px !important' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kitPriceRows.map((row) => {
                    const kitOpt = kits.find((k) => Number(k.id) === Number(row.kit)) || null;
                    return (
                      <TableRow key={row.tempKey} hover>
                        <TableCell>
                          <Autocomplete
                            size="small"
                            options={kits.map((k) => ({ id: k.id, label: k.name }))}
                            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                            getOptionLabel={(o) => o.label || ''}
                            value={kitOpt ? { id: kitOpt.id, label: kitOpt.name } : null}
                            onChange={(_, opt) => {
                              setKitPriceRows((prev) =>
                                prev.map((r) =>
                                  r.tempKey === row.tempKey
                                    ? { ...r, kit: opt ? opt.id : '', kit_name: opt ? opt.label : '' }
                                    : r,
                                ),
                              );
                            }}
                            renderInput={(params) => <TextField {...params} placeholder="Оберіть комплект" />}
                            sx={{ minWidth: 0, width: '100%' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={row.price_form1}
                            onChange={(e) =>
                              setKitPriceRows((prev) =>
                                prev.map((r) => r.tempKey === row.tempKey ? { ...r, price_form1: e.target.value } : r)
                              )
                            }
                            inputProps={{ min: 0, step: 'any' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={row.price_form2}
                            onChange={(e) =>
                              setKitPriceRows((prev) =>
                                prev.map((r) => r.tempKey === row.tempKey ? { ...r, price_form2: e.target.value } : r)
                              )
                            }
                            inputProps={{ min: 0, step: 'any' }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" color="error" onClick={() =>
                            setKitPriceRows((prev) => prev.filter((r) => r.tempKey !== row.tempKey))
                          }>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
          <SectionHeader icon={<NotesIcon color="primary" fontSize="small" />} title="Примітки" />
          <TextField
            label="Вільний текст"
            fullWidth
            multiline
            minRows={3}
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
            helperText="Додаткова інформація, неструктуровані реквізити"
          />
        </Paper>
      </Stack>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          mt: 3,
          py: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 2,
          zIndex: 1,
        }}
      >
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Збереження…' : 'Зберегти'}
        </Button>
        {!isNew && (
          <Button color="error" variant="outlined" onClick={() => setDeleteConfirmOpen(true)}>
            Видалити
          </Button>
        )}
      </Box>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Видалити замовника?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Замовника «{form.name}» буде видалено з довідника. Пов’язані замовлення залишаться з текстовим
            полем «Замовник» без прив’язки до довідника.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Скасувати
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={async () => {
              try {
                setDeleting(true);
                await customerDirectoryService.delete(id);
                navigate('/customer-orders?tab=customers');
              } catch (e) {
                console.error(e);
                setDeleteConfirmOpen(false);
                setSnackbar({ open: true, message: 'Не вдалося видалити', severity: 'error' });
              } finally {
                setDeleting(false);
              }
            }}
          >
            {deleting ? 'Видалення…' : 'Видалити'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CustomerEditPage;
